import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import moment from 'moment-mini';
import {
    Address, ADDRESS_DEFAULT_FIELD_LENGTH, Application, APPLICATION_DEFAULT_FIELD_LENGTH, BUSINESS_ACTIVITY_SURVEY_FIELD_LENGTH, BusinessActivitySurvey, Company, COMPANY_DEFAULT_FIELD_LENGTH, CONTACT_DEFAULT_FIELD_LENGTH,
    ContactData, DEFAULT_STRING_MAX_LENGTH,
    EXPECTED_ACTIVITY_SURVEY_FIELD_LENGTH,
    ExpectedActivitySurvey
} from './models/data-model';

const piva = /^(it|IT)?\d{11}$/;
const eircode = /^([AC-FHKNPRTV-Y]\d{2}|D6W)(\s)?[0-9AC-FHKNPRTV-Y]{4}$/i;
const codicePostale = /^\d{5}$/;
const ukPostCode = /^[A-z](\d|\d[A-z0-9]|[A-z]\d|[A-z]\d[A-z0-9])\s?\d[A-z]{2}$/i;
const netherlandsPostcode = /^\d{4}\s?([A-RT-Z][A-Z]|S[B-CE-RT-Z])$/i;
const germanPostcode = /^\d{5}$/;
const frenchPostcode = /^\d{5}/; // allowing chars after the 5 digits on purpose, see CEDEX
const spanishPostcode = /^\d{5}$/;
const emailLocalPartWhitelist = /^([a-zA-Z\d.\-_+&*])*$/;
const emailSubdomainsWhitelist = /^([a-zA-Z\d\-.])*$/;
const emailTopLevelDomainWhitelist = /^([a-zA-Z])*$/;
const singleAtInTheMiddle = /^[^@]+@[^@]+$/;

export const regexValidators = {
    // Digits after the ICC.
    phoneWithoutICC: /^\d{5,12}$/,
    piva: piva,
    rea: /^[A-Z]{2}-\d+$/,
    postcodesByCountry: {
        IRL: eircode,
        ITA: codicePostale,
        GBR: ukPostCode,
        NLD: netherlandsPostcode,
        DEU: germanPostcode,
        FRA: frenchPostcode,
        ESP: spanishPostcode
    }
};

export class CustomValidators {

    private static italianMobileValidator(number): ValidationErrors | null {
        const error = { notValidNumber: true };
        if (!number.startsWith('3')) {
            return error;
        }
        if (number.length < 9 || number.length > 10) {
            return error;
        }
        return null;
    }

    private static britishMobileValidator(number): ValidationErrors | null {
        const error = { notValidNumber: true };
        if (!number.startsWith('7')) {
            return error;
        }
        if (number.length != 10) {
            return error;
        }
        return null;
    }

    public static phoneValidatorFactory(prefixControlName, numberControlName, required: boolean = true): ValidatorFn {
        return (AC: AbstractControl) => {
            const prefixControl = AC.get(prefixControlName);
            const numberControl = AC.get(numberControlName);
            const phoneWithoutICCRegex = new RegExp(regexValidators.phoneWithoutICC);

            if (numberControl.value) {
                if (!phoneWithoutICCRegex.test(numberControl.value)) {
                    return {
                        'notValidNumber': true
                    };
                }

                switch (prefixControl.value) {
                    case "+39":
                        return this.italianMobileValidator(numberControl.value);
                    case "+44":
                        return this.britishMobileValidator(numberControl.value);
                }
            }

            if (required && !(numberControl.value && prefixControl.value)) {
                return {
                    "required": true
                };
            }

            return null;
        };
    }

    public static dateValidatorFactory(minAge?: number, maxAge?: number): ValidatorFn {
        const now = moment();
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) {
                return null;
            }
            const date = moment(control.value, 'DD/MM/YYYY', true);
            if (!date.isValid()) {
                return {
                    "invalidDateFormat": true
                };
            } else if ((minAge && now.diff(date, 'years') < minAge) || (maxAge && now.diff(date, 'years') > maxAge)) {
                return {
                    "ageNotAllowed": true
                };
            }
            return null;
        };
    }

    public static noSoldoPecAddressValidatorFactory(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {

            if (control.value === "fatture@pec.soldo.com") {
                return { "notYourEmail": true };
            }

            return null;
        };
    }

    public static emailValidatorFactory(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const email: string = control.value;
            if (!email) {
                return null;
            }
            const invalidEmailError = { invalidEmailFormat: true };
            if (!singleAtInTheMiddle.exec(email) || email.includes('..')) {
                return invalidEmailError;
            }
            const { local, subdomains, tld } = CustomValidators.splitEmail(email);
            if (local.length === 0) {
                return invalidEmailError;
            }
            if (!emailLocalPartWhitelist.exec(local)) {
                return invalidEmailError;
            }
            if (subdomains.length === 0) {
                return invalidEmailError;
            }
            if (!emailSubdomainsWhitelist.exec(subdomains)) {
                return invalidEmailError;
            }
            if (CustomValidators.noDotAtStartOrEnd(local) || CustomValidators.noDotAtStartOrEnd(subdomains)) {
                return invalidEmailError;
            }
            if (tld.length < 2) {
                return invalidEmailError;
            }
            if (!emailTopLevelDomainWhitelist.exec(tld)) {
                return invalidEmailError;
            }

            return null;
        };
    }

    private static noDotAtStartOrEnd(part: string) {
        return part.startsWith('.') || part.endsWith('.');
    }

    private static splitEmail(email: string): { local: string, subdomains: string, tld: string } {
        const parts = email.split('@');
        const subdomains = parts[1].split('.');
        const tld = subdomains.pop();
        return {
            local: parts[0],
            subdomains: subdomains.join('.'),
            tld: tld
        };
    }

    public static getApplicationMaxlength(field: keyof Application): number {
        return APPLICATION_DEFAULT_FIELD_LENGTH[field] || DEFAULT_STRING_MAX_LENGTH;
    }

    public static getContactMaxlength(field: keyof ContactData): number {
        return CONTACT_DEFAULT_FIELD_LENGTH[field] || DEFAULT_STRING_MAX_LENGTH;
    }

    public static getAddressMaxlength(field: keyof Address): number {
        return ADDRESS_DEFAULT_FIELD_LENGTH[field] || DEFAULT_STRING_MAX_LENGTH;
    }

    public static getCompanyMaxlength(field: keyof Company): number {
        return COMPANY_DEFAULT_FIELD_LENGTH[field] || DEFAULT_STRING_MAX_LENGTH;
    }

    public static getBusinessActivitySurveyMaxlength(field: keyof BusinessActivitySurvey): number {
        return BUSINESS_ACTIVITY_SURVEY_FIELD_LENGTH[field] || DEFAULT_STRING_MAX_LENGTH;
    }

    public static getExpectedActivitySurveyMaxlength(field: keyof ExpectedActivitySurvey): number {
        return EXPECTED_ACTIVITY_SURVEY_FIELD_LENGTH[field] || DEFAULT_STRING_MAX_LENGTH;
    }

    public static urlValidator(): ValidatorFn {
        const urlPattern = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(\/[\w\-.~:/?#[\]@!$&'()*+,;=%]*)?$/i;
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) {
                return null;
            }
            return urlPattern.test(control.value) ? null : { pattern: true };
        };
    }
}

